import React, { useContext, useState } from 'react';
import { RefreshCw, AlertTriangle } from 'lucide-react';
import { Context } from 'Context/Context';
import { retryValuationApi, retryFirmMatchApi, retryPartyResearchApi } from 'Constants';
import { toast } from 'react-toastify';
import QcrLoader from 'Components/Icons/QcrLoader';

// Shown in place of AI content that hasn't completed yet (still generating,
// or failed) for any of the three AI calls - adverse party research, case
// valuation, and firm matching. Wires directly into the existing retry
// endpoints (POST /case/:caseId/retry/valuation, /retry/firmMatch,
// /retry/partyResearch), which are already rate-limited server-side
// (checkRetryLimit) - a 429 response is surfaced as a toast rather than
// treated as a hard error. type="research" retries the whole pipeline in
// one click (research, then automatically valuation + firm-match) since
// research failing means neither of the other two calls has run yet.
const RETRY_API_BY_TYPE = {
  valuation: retryValuationApi,
  firmMatch: retryFirmMatchApi,
  research: retryPartyResearchApi,
};

function AiRetryNotice({ type, status, caseId, setUpdateComp, label }) {
  const { makeApiCallFunc } = useContext(Context);
  const [isRetrying, setIsRetrying] = useState(false);

  // 2 = COMPLETED - nothing to show once the data is actually in.
  if (status === 2 || !caseId) return null;

  const isFailed = status === 3;
  const apiUrl = (RETRY_API_BY_TYPE[type] || retryValuationApi).replace(':caseId', caseId);

  const handleRetry = () => {
    if (isRetrying) return;
    setIsRetrying(true);
    makeApiCallFunc(
      apiUrl,
      {},
      () => {
        setIsRetrying(false);
        toast.success(`Retrying the ${label}…`, { position: 'top-right', autoClose: 3000 });
        setUpdateComp((prev) => !prev);
      },
      (error) => {
        setIsRetrying(false);
        const message = error?.response?.data?.error || `Couldn't retry the ${label}. Please try again.`;
        toast.error(message, { position: 'top-right', autoClose: 4000 });
      },
      null,
      true
    );
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-[10px] border border-[rgba(20,33,61,.12)] bg-ivory px-4 py-3 mb-4 font-plexsans">
      <div className="flex items-center gap-2.5 min-w-0">
        {isFailed ? (
          <AlertTriangle size={16} className="text-tan flex-none" />
        ) : (
          <QcrLoader variant="draw" size={18} />
        )}
        <p className="text-[13px] lg:text-[13.5px] text-[rgba(20,33,61,.75)] truncate">
          {isFailed ? `We couldn't generate the ${label}.` : `Still generating the ${label}…`}
        </p>
      </div>
      {/* Retry only ever applies to a call that actually failed - while it's
          still in progress (or hasn't started), there's nothing to retry yet
          and firing one here would just kick off a duplicate Gemini call. */}
      {isFailed && (
        <button
          type="button"
          onClick={handleRetry}
          disabled={isRetrying}
          className="flex-none flex items-center gap-1.5 rounded-lg border border-[rgba(20,33,61,.15)] px-3.5 py-1.5 text-[12.5px] font-semibold text-navy hover:border-tan transition-colors disabled:opacity-50"
        >
          <RefreshCw size={13} className={isRetrying ? 'animate-spin' : ''} />
          {isRetrying ? 'Retrying…' : 'Retry'}
        </button>
      )}
    </div>
  );
}

export default AiRetryNotice;
